import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { MEDIA_CONFIG } from '../data/media';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

function Contact() {
  const [formData, setFormData] = useState({
    from_name: '',
    reply_to: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const validateForm = () => {
    if (!formData.from_name || !formData.reply_to || !formData.subject || !formData.message) {
      setFormError('Bitte füllen Sie alle Felder aus.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.reply_to)) {
      setFormError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Debug: Log form data before sending
      console.log('Form data before sending:', formData);

      const result = await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current!,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log('EmailJS response:', result);

      if (result.text === 'OK') {
        setFormSubmitted(true);
        setFormData({
          from_name: '',
          reply_to: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setFormError('Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <img
          src={`${MEDIA_CONFIG.paths.backgrounds}/headers/contact-header.jpg`}
          alt="Kontakt Header"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Kontakt</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam Ihr nächstes Projekt verwirklichen
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-zinc-900 p-8 rounded-lg">
            <h2 className="text-2xl font-semibold mb-6">Schreiben Sie mir</h2>
            {formSubmitted ? (
              <div className="bg-green-900/30 border border-green-800 rounded-md p-6 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <h3 className="text-xl font-medium mb-2">Nachricht gesendet!</h3>
                <p className="text-gray-300 mb-4">
                  Ihre Nachricht hat uns erfolgreich erreicht. Wir melden uns so schnell wie möglich bei Ihnen.
                </p>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="px-6 py-2 bg-accent-600 text-white font-medium rounded-md hover:bg-accent-700 transition-colors"
                >
                  Neue Nachricht
                </button>
              </div>
            ) : (
              <form ref={formRef} className="space-y-6" onSubmit={handleSubmit}>
                {formError && (
                  <div className="bg-red-900/30 border border-red-800 rounded-md p-4 text-sm text-red-200">
                    {formError}
                  </div>
                )}
                <div>
                  <label htmlFor="from_name" className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    id="from_name"
                    name="from_name"
                    value={formData.from_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-black border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reply_to" className="block text-sm font-medium mb-2">E-Mail</label>
                  <input
                    type="email"
                    id="reply_to"
                    name="reply_to"
                    value={formData.reply_to}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-black border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-2">Betreff</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-black border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">Nachricht</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-black border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-3 bg-accent-600 text-white font-medium rounded-md hover:bg-accent-700 transition-colors flex items-center justify-center ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    'Nachricht senden'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div>
            <div className="bg-zinc-900 p-8 rounded-lg mb-8">
              <h2 className="text-2xl font-semibold mb-6">Kontaktinformationen</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 mt-1 mr-4 text-accent-500" />
                  <div>
                    <h3 className="font-medium">E-Mail</h3>
                    <p className="text-gray-400">info@honzamedia.de</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="w-6 h-6 mt-1 mr-4 text-accent-500" />
                  <div>
                    <h3 className="font-medium">Telefon</h3>
                    <p className="text-gray-400">+49 (0) 179 432 34 36</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Auch per WhatsApp & Telegram erreichbar
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 mt-1 mr-4 text-accent-500" />
                  <div>
                    <h3 className="font-medium">Standort</h3>
                    <p className="text-gray-400">Schloß-Holte Stukenbrock</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="w-6 h-6 mt-1 mr-4 text-accent-500" />
                  <div>
                    <h3 className="font-medium">Verfügbarkeit</h3>
                    <p className="text-gray-400">
                      Mo - Fr: 9:00 - 18:00 Uhr<br />
                      Sa - So: Nach Vereinbarung
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;